import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Collects many small props (each a geometry, a color and a placement) and merges them into one mesh
 * painted with vertex colors: a whole graveyard, every jack-o'-lantern, all the candles are one draw call.
 */
export class Kit {
  private geos: THREE.BufferGeometry[] = [];
  private c = new THREE.Color();
  private tmp = new THREE.Matrix4();

  /**
   * `extras`: per-piece constant vertex attributes (one float each) and their defaults, for shaders
   * that move or light pieces differently inside one merged mesh (see engine/districts.ts).
   */
  constructor(private extras: Record<string, number> = {}) {}

  get size(): number {
    return this.geos.length;
  }

  /** Adds `geo` (not consumed) placed by `m`, then by `local` if given (m × local). */
  add(geo: THREE.BufferGeometry, color: THREE.ColorRepresentation, m: THREE.Matrix4, local?: THREE.Matrix4, vals?: Record<string, number>): void {
    const g = geo.index ? geo.toNonIndexed() : geo.clone();
    g.applyMatrix4(local ? this.tmp.multiplyMatrices(m, local) : m);
    for (const k of Object.keys(g.attributes)) if (k !== 'position' && k !== 'normal' && k !== 'uv') g.deleteAttribute(k);
    if (!g.getAttribute('normal')) g.computeVertexNormals();
    const n = g.getAttribute('position').count;
    if (!g.getAttribute('uv')) g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
    this.c.set(color);
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      col[i * 3] = this.c.r;
      col[i * 3 + 1] = this.c.g;
      col[i * 3 + 2] = this.c.b;
    }
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    for (const [k, d] of Object.entries(this.extras)) g.setAttribute(k, new THREE.Float32BufferAttribute(new Float32Array(n).fill(vals?.[k] ?? d), 1));
    g.morphAttributes = {};
    g.clearGroups();
    this.geos.push(g);
  }

  /** The merged mesh (null when empty). The pieces are disposed. */
  build(mat: THREE.Material, name: string): THREE.Mesh | null {
    if (this.geos.length === 0) return null;
    const merged = mergeGeometries(this.geos, false);
    for (const g of this.geos) g.dispose();
    this.geos = [];
    if (!merged) return null;
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mat);
    mesh.name = name;
    return mesh;
  }
}

/** Placement helper: position, yaw, optional tilt and uniform or per-axis scale. */
export const place = (x: number, y: number, z: number, yaw = 0, s: number | THREE.Vector3 = 1, tiltX = 0, tiltZ = 0): THREE.Matrix4 => {
  const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(tiltX, yaw, tiltZ, 'YXZ'));
  const sv = typeof s === 'number' ? new THREE.Vector3(s, s, s) : s;
  return new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), q, sv);
};
